import { env } from '../config/env'
import type { Customer, FieldDef } from '../domain/types'
import type { BookingInput, BookingReceipt, BookingStatus, ScheduleAvailability, ScheduleDay, SchedulePayment, ScheduleService, ScheduleTime } from '../domain/scheduling'
import { scheduleDate, scheduleInstant } from '../domain/scheduling'
import type { DataSource } from './DataSource'
import { http, apiV1, ticketing, lid, pid } from './http/client'
import { ApiError } from './errors'

export interface SchedulingSource {
  services(): Promise<ScheduleService[]>
  serviceNote(serviceId: number): Promise<string>
  days(serviceId: number, month: number): Promise<ScheduleDay[]>
  availability(serviceId: number, date: string): Promise<ScheduleAvailability>
  nextDay(serviceId: number): Promise<string | null>
  times(serviceId: number, sessionId: number, resourceId: number): Promise<ScheduleTime[]>
  payment(serviceId: number, sessionId: number): Promise<SchedulePayment>
  customerFields(fields: FieldDef[], customerId: number): Promise<Record<string, string>>
  book(input: BookingInput): Promise<BookingReceipt>
  batch(file: File, serviceId: number, sessionId: number, attendanceType: number): Promise<BookingReceipt>
  status(aggregatorId: string): Promise<BookingStatus>
  waitlist(serviceId: number, customer: Customer): Promise<void>
}
interface Response<T> { responseData: T; messages?: { type: string; description?: string; code?: string }[] }
function receipt(response: Response<BookingReceipt>): BookingReceipt {
  const failure = response.messages?.find((m) => m.type.toUpperCase() === 'ERROR')
  if (failure || !response.responseData?.aggregatorId) {throw new ApiError(400, failure?.code ?? null, failure?.description ?? 'Não foi possível enviar o agendamento. Confira os dados e tente novamente.')}
  return response.responseData
}
export class RealSchedulingSource implements SchedulingSource {
  constructor(private resolveServiceConfig: (serviceId: number) => Promise<number | undefined>) {}
  private base = () => `scheduling/schedules/providers/${pid()}`
  private service = (id: number) => `${this.base()}/locations/${lid()}/services/${id}`
  services = () => http.get<ScheduleService[]>('v1', apiV1(`locations/${lid()}/services`))
  async serviceNote(serviceId: number): Promise<string> {
    const serviceConfigId = await this.resolveServiceConfig(serviceId)
    if (!serviceConfigId) {return ''}
    const config = await http.get<{ serviceNote?: string | null }>('v1', apiV1(`locations/${lid()}/serviceConfigs/${serviceConfigId}`))
    return config.serviceNote ?? ''
  }
  days = (id: number, month: number) => http.get<ScheduleDay[]>('v2', `${this.base()}/services/${id}/available-session-days`, { referenceMonth: month })
  availability = (id: number, date: string) => http.get<ScheduleAvailability>('v2', `${this.service(id)}/sessions-resources-by-service`, { date: `${date}T00:00:00-03:00` })
  async nextDay(id: number) {
    const result = await http.get<{ date?: string | null }>('v2', `${this.service(id)}/next-available-session-day`)
    return result?.date ? scheduleDate(new Date(scheduleInstant(result.date))) : null
  }
  times = (serviceId: number, sessionId: number, resourceId: number) => http.get<ScheduleTime[]>('v2', `${this.base()}/locations/${lid()}/resources/${resourceId}/sessions/${sessionId}/services/${serviceId}/available-time`)
  payment = (serviceId: number, sessionId: number) => http.get<SchedulePayment>('v1', apiV1(`sessions/${sessionId}/service/${serviceId}/payment-support`))
  async customerFields(fields: FieldDef[], customerId: number) {
    const values = await Promise.all(fields.map(async (f) => {
      const result = await http.get<{ value?: string; data?: { value?: string } }>('v1', apiV1(`locations/${lid()}/custom-datas/${f.key.replace('customField:', '')}/customer/${customerId}`))
      return [f.key, result.value ?? result.data?.value ?? '']
    }))
    return Object.fromEntries(values)
  }
  async book(input: BookingInput) {
    const { customer } = input
    return receipt(await http.post<Response<BookingReceipt>>('v2', 'ticketing/tickets', {
      pid: Number(pid()), locationId: Number(lid()), schedulingChannel: 0,
      customer: { ...(customer.id > 0 ? { id: customer.id } : {}), name: customer.nome.trim(), email: customer.email, phone: customer.telefone.replace(/\D/g, '') },
      tickets: [{ serviceId: input.serviceId, sessionId: input.sessionId, resourceId: input.resourceId,
        bookedStartTime: input.bookedStartTime, doFit: input.doFit, attendanceType: input.attendanceType,
        priority: input.priority, customData: input.customData }],
      payment: input.payment.hasPayment && input.payment.realPrice > 0 ? { data: [], price: input.payment.realPrice, method: 1 } : null,
    }))
  }
  async batch(file: File, serviceId: number, sessionId: number, attendanceType: number) {
    const body = new FormData()
    body.append('file', file)
    body.append('jsonString', JSON.stringify({ providerId: Number(pid()), locationId: Number(lid()), serviceId, sessionId, attendanceType }))
    return receipt(await http.post<Response<BookingReceipt>>('v2', 'ticketing/tickets/batch', body))
  }
  async status(id: string): Promise<BookingStatus> {
    // The legacy /status endpoint counts Opened as Error. Read ticket states
    // to distinguish asynchronous processing from an actual rejection.
    const response = await http.get<Response<{ aggregator: { tickets: { status: string | number }[] } }>>('v2', ticketing(`locations/${lid()}/aggregators/${encodeURIComponent(id)}`))
    const tickets = response.responseData?.aggregator?.tickets
    if (!tickets?.length) {return { pending: 1, error: 0, success: 0 }}
    return tickets.reduce((counts, ticket) => {
      const status = String(ticket.status).toLowerCase()
      if (['unauthorized', '9', 'canceled', '7'].includes(status)) {counts.error++}
      else if (['authorized', 'confirmed', 'checked', 'called', 'started', 'completed', 'reopened', '1', '2', '3', '4', '5', '6', '8'].includes(status)) {counts.success++}
      else {counts.pending++}
      return counts
    }, { pending: 0, error: 0, success: 0 })
  }
  async waitlist(serviceId: number, customer: Customer) {
    await http.post('v1', 'api/overbooking', { serviceId, name: customer.nome, email: customer.email, phone: customer.telefone.replace(/\D/g, ''), ...(customer.id > 0 ? { id: customer.id } : {}) })
  }

}

// Offline demo uses the same Journey store, so bookings appear in Atendimentos.
export function createSchedulingSource(ds: DataSource): SchedulingSource {
  if (env.dataSource === 'real') {return new RealSchedulingSource(async (serviceId) =>
    (await ds.getReferenceData()).serviceConfigs.find((config) => config.serviceIds.includes(serviceId))?.serviceConfigId)}
  const queueFor = async (id: number) => {
    const ref = await ds.getReferenceData()
    const queue = ref.serviceConfigs.find((c) => c.serviceIds.includes(id))
    if (!queue) {throw new Error('Serviço sem fila disponível.')}
    return queue
  }
  return {
    services: async () => (await ds.getReferenceData()).services.map((s) => ({ id: s.serviceId, name: s.nome, serviceConfigType: 0 })),
    serviceNote: async () => '',
    days: async (id, month) => (await ds.getAvailableDays((await queueFor(id)).serviceConfigId)).filter((d) => Number(d.date.slice(5, 7)) === month).map((d) => ({ date: d.date, hasSlotLeft: d.availableCount > 0 })),
    availability: async (id, date) => ({ resources: [], sessions: (await ds.getDaySlots((await queueFor(id)).serviceConfigId, date)).map((s) => ({ id: s.sessionId, start: s.start, end: s.end, capacity: s.capacity ?? 0, utilization: (s.capacity ?? 0) - (s.remainingCapacity ?? 0), hasSlotsLeft: s.available, serviceConfigType: 0, attendenceServiceType: 0 })) }),
    nextDay: async (id) => (await ds.getAvailableDays((await queueFor(id)).serviceConfigId))[0]?.date ?? null,
    times: async () => [], payment: async () => ({ hasPayment: false, realPrice: 0 }), customerFields: async () => ({}),
    book: async (input) => {
      const customer = input.customer.id > 0 ? input.customer : await ds.createCustomer(input.customer)
      const agg = await ds.createJourney({ cliente: customer, templateKey: null, templateId: null, metadata: {}, priority: input.priority,
        itens: [{ serviceId: input.serviceId, serviceConfigId: (await queueFor(input.serviceId)).serviceConfigId, sessionId: input.sessionId, sequence: 0, customFields: input.customData.map((f) => ({ id: f.customFieldId, value: f.value })) }] })
      return { aggregatorId: agg.aggregatorId }
    },
    batch: async () => {throw new Error('Importação de planilha disponível no ambiente conectado.')},
    status: async () => ({ pending: 0, error: 0, success: 1 }),
    waitlist: async () => {},

  }
}
